import { getTime, millisecondsToSeconds } from "~/tools/date-time";
import { Tuple } from "~/tools/tuple";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { GetLabelValuesSuccess, GetLabelsSuccess } from "../../common/openapi";
import { request } from "../../generic/request";
import type { UseFullQueryOptions } from "../react-query";

export interface GetMetricsLabelsParams {
	timeRange: TimeRange;
	matches?: ReadonlyArray<string>;
}

export const queryMetricsLabels = (({ timeRange, matches = [] }) => {
	async function queryFn() {
		const now = Date.now();

		const { data, error } = await request<GetLabelsSuccess>("/api/metrics/labels", {
			query: {
				start: millisecondsToSeconds(getTime(timeRange.start(now))).toString(),
				end: millisecondsToSeconds(getTime(timeRange.endDate(now))).toString(),
				"match[]": matches.slice(),
			},
			queryOptions: {
				/**
				 * It can't use "bracket" because the stringified query will have 'match[]=...'
				 * instead of escaped 'match%5B%5D=...'
				 */
				arrayFormat: "none",
			},
		});

		if (error) {
			throw new Error(error);
		}

		return data ?? [];
	}

	return {
		queryKey: Tuple("metrics", "labels", { matches }),
		queryFn,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		timeRange,
	} as const;
}) satisfies (params: GetMetricsLabelsParams) => UseFullQueryOptions;

export interface GetMetricsValuesParams {
	label: string;
	timeRange: TimeRange;
	matches?: ReadonlyArray<string>;
}

export const queryMetricsLabelValues = (({ label, timeRange, matches = [] }) => {
	async function queryFn() {
		const now = Date.now();

		const { data, error } = await request<GetLabelValuesSuccess>(`/api/metrics/label/${label}/values`, {
			query: {
				start: millisecondsToSeconds(getTime(timeRange.start(now))).toString(),
				end: millisecondsToSeconds(getTime(timeRange.endDate(now))).toString(),
				"match[]": matches.slice(),
			},
			queryOptions: {
				/**
				 * It can't use "bracket" because the stringified query will have 'match[]=...'
				 * instead of escaped 'match%5B%5D=...'
				 */
				arrayFormat: "none",
			},
		});

		if (error) {
			throw new Error(error);
		}

		return data ?? [];
	}

	return {
		queryKey: Tuple("metrics", "labels", label, "values", { matches }),
		queryFn,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		timeRange,
	} as const;
}) satisfies (params: GetMetricsValuesParams) => UseFullQueryOptions;
